/**
 * One picture as the sheet controls show it (spec 035): its decoded image
 * (source and natural size, through the preview loader), its name and its
 * spritesheet cut (the media sheet store, read with `GET /media/:id`).
 */
import { useEffect, useState } from 'react';
import type { SheetCut, SpriteSize } from '../../../../Shared/src/@types';
import { apiClient } from '../../api/client';
import { loadPreviewImage } from '../../utils/previewMedia';
import { useMediaSheetStore } from '../../stores/mediaSheetStore';

export interface PictureInfo {
  src?: string;
  natural?: SpriteSize;
  name?: string;
  cut?: SheetCut;
  /** The cut has been read (with or without one). */
  known: boolean;
  /** The image has been decoded, or could not be. */
  loaded: boolean;
}

export function usePicture(mediaId: number | undefined): PictureInfo {
  const id = typeof mediaId === 'number' && mediaId > 0 ? mediaId : 0;
  const cut = useMediaSheetStore((s) => (id ? s.sheets[id] : undefined));
  const known = useMediaSheetStore((s) => (id ? !!s.known[id] : true));
  const name = useMediaSheetStore((s) => (id ? s.names[id] : undefined));
  const [image, setImage] = useState<{ id: number; src?: string; natural?: SpriteSize; loaded?: boolean }>({ id: 0 });

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    void useMediaSheetStore.getState().loadSheets([id]);
    void loadPreviewImage(apiClient, id).then((img) => {
      if (cancelled) return;
      if (!img) {
        setImage({ id, loaded: true });
        return;
      }
      const natural = img.naturalWidth > 0 && img.naturalHeight > 0 ? { width: img.naturalWidth, height: img.naturalHeight } : undefined;
      setImage({ id, src: img.src, natural, loaded: true });
    });
    return () => { cancelled = true; };
  }, [id]);

  const mine = image.id === id ? image : { id, src: undefined, natural: undefined, loaded: false };
  return { src: mine.src, natural: mine.natural, name, cut, known, loaded: !id || !!mine.loaded };
}
